import React, {useEffect, useRef, useState} from "react";
import {
    Button,
    Space,
    message,
    Modal,
    Upload,
    Descriptions,
    Divider,
    Select as AntSelect, Popconfirm, Typography, Progress, Alert
} from "antd";
import {CheckOutlined, LoadingOutlined, PlusOutlined, UploadOutlined} from "@ant-design/icons";
import HelpPopover from "../HelpPopover";
import {Can} from "../helpers/Can";
import api from "../api";
import useConsumer from "../../channels/consumer";
import {useTranslation} from "react-i18next";
import useOnCloseDirty from "../helpers/useOnCloseDirty";
import VerticalSpace from "@/components/helpers/VerticalSpace";
import TagImportsTable from "./TagImportsTable";

const TAG_FIELDS = {
    // Core
    name: {name: 'Name', group: 'core'},
    sub_type: {name: 'Type', group: 'core'},
    list: {name: 'List', group: 'core'},
    description: {name: 'Description', group: 'core'},
    link: {name: 'Link', group: 'core'},
    date_start: {name: 'Date Start', group: 'core'},
    date_end: {name: 'Date End', group: 'core'},

    // Location
    location_country: {name: 'Country', group: 'location'},
    location_country_code: {name: 'Country Code', group: 'location'},
    location_state: {name: 'State', group: 'location'},
    location_city: {name: 'City', group: 'location'},
    location_name: {name: 'Location Name', group: 'location'},

    // Product
    featured_organization_name: {name: 'Featured Organization', group: 'product'},
    gtin: {name: 'GTIN', group: 'product'},
    sku: {name: 'SKU', group: 'product'},

    // Artwork
    artwork_title: {name: 'Artwork Title', group: 'artwork'},
    artwork_creator: {name: 'Artwork Creator', group: 'artwork'},
    artwork_date_created: {name: 'Artwork Date Created', group: 'artwork'},
    artwork_medium: {name: 'Artwork Medium', group: 'artwork'},
    artwork_style_period: {name: 'Artwork Style/Period', group: 'artwork'},
    artwork_source: {name: 'Artwork Source', group: 'artwork'},
    artwork_inventory_number: {name: 'Artwork Inventory Number', group: 'artwork'},
    artwork_copyright_notice: {name: 'Artwork Copyright Notice', group: 'artwork'},
    artwork_circa_date_created: {name: 'Artwork Circa Date Created', group: 'artwork'},
    artwork_content_description: {name: 'Artwork Content Description', group: 'artwork'},
    artwork_contribution_description: {name: 'Artwork Contribution Description', group: 'artwork'},
    artwork_creator_id: {name: 'Artwork Creator ID', group: 'artwork'},
    artwork_copyright_owner_id: {name: 'Artwork Copyright Owner ID', group: 'artwork'},
    artwork_licensor_id: {name: 'Artwork Licensor ID', group: 'artwork'},
    artwork_licensor_name: {name: 'Artwork Licensor Name', group: 'artwork'},
    artwork_physical_description: {name: 'Artwork Physical Description', group: 'artwork'},
    artwork_source_inventory_url: {name: 'Artwork Source Inventory URL', group: 'artwork'},
    collection_management_system: {name: 'Collection Management System', group: 'artwork'},
    cms_id: {name: 'CMS ID', group: 'artwork'},
};

export default ({onShowImportTags, q, onLoaded, newTagImport, refresh}) => {
    return (
        <TagImportsTable q={q} onLoaded={onLoaded} newTagImport={newTagImport} refresh={refresh} onShowImportTags={onShowImportTags}/>
    );
}

const NewTagImportButton = ({onCreate, onImportDone}) => {
    const {t} = useTranslation();
    const [modalVisible, setModalVisible] = useState()
    const onClick = () => {
        setTagImport()
        setModalVisible(true)
    }

    const onCloseDirty = useOnCloseDirty(() => setModalVisible(false))

    const [fileList, setFileList] = useState([])
    const [uploading, setUploading] = useState()

    const onAddFile = (file) => {
        setUploading(true)

        const data = new FormData()
        data.append('tag_import[file]', file)

        api.post('/api/tag_imports', data).then(res => {
            setUploading(false)
            setFileList([])
            setTagImport(res.data)
            onCreate && onCreate(res.data)
        }).catch(err => {
            setUploading(false)
            message.error(`Error: ${err}`)
        })

        return false
    }

    const [tagImport, setTagImport] = useState()

    return (<>
        <Button type='primary' onClick={onClick}>
            <PlusOutlined/>
            {t('button-new-tag-import', 'New Tag Import')}
        </Button>
        <Modal
            title={<>{t('new-tag-import', 'New Tag Import')} <HelpPopover code={'new-tag-import'}/></>}
            open={modalVisible}
            destroyOnClose
            onCancel={() => onCloseDirty(false)}
            centered
            footer={null}
        >
            {tagImport ? <TagImportSetup tagImport={tagImport} onDone={() => { setModalVisible(false); onImportDone && onImportDone(); }}/> : (
                <form id={'tag-import-form'}>
                    <Upload
                        beforeUpload={onAddFile}
                        fileList={fileList}
                        showUploadList={false}
                    >
                        <Button icon={<UploadOutlined/>} loading={uploading} block>{uploading ? t('uploading', 'Uploading...') : t('select-file', 'Select File...')}</Button>
                    </Upload>
                    <p style={{margin: '1em 0 0'}}><em>{t('tag-import-supported-filetypes', 'Supported file types: .csv, .tsv, .xls, .xlsx')}</em></p>
                </form>
            )}
        </Modal>
    </>);
}

const TagImportSetup = ({tagImport, onDone}) => {
    const {t} = useTranslation();
    const [attrs, setAttrs] = useState(tagImport)

    const consumer = useConsumer();
    const userSub = useRef();

    useEffect(() => {
        api(`/api/tag_imports/${tagImport.id}`).then(res => setAttrs(res.data))

        if (!userSub.current) {
            userSub.current = consumer?.subscriptions?.create({channel: "UserChannel"}, {
                received: (data) => {
                    switch (data.type) {
                        case 'tagImport':
                            if (data.attrs.id == tagImport.id) setAttrs(prev => ({...prev, ...data.attrs}))
                            return

                        case 'tagImportProgress':
                            if (data.id == tagImport.id) {
                                if (data.done) {
                                    message.success(t('message-processing-Done', 'Processing Done!'))
                                    onDone && onDone()
                                }
                                setProgress(data.progress)
                            }
                            return
                    }
                }
            });
        }

        return () => {
            userSub.current?.unsubscribe()
        }
    }, [tagImport?.id])

    const onChange = (column, value) => {
        column.mapping = value
        api.put(`/api/tag_imports/${tagImport.id}/mapping`, {column}).then(res => {
            message.success(t('message-tag-import-updated', 'Tag Import Updated!'))
            setAttrs(res.data);
        })
    }

    const update = (field) => {
        return (value) => {
            const tag_import = {}
            tag_import[field] = value
            api.put(`/api/tag_imports/${tagImport.id}`, {tag_import}).then(res => {
                message.success(t('message-tag-import-updated', 'Tag Import Updated!'))
                setAttrs(res.data);
            })
        }
    }

    const [loading, setLoading] = useState()
    const [progress, setProgress] = useState()
    const [processing, setProcessing] = useState()
    const process = () => {
        setLoading(true)
        api.post(`/api/tag_imports/${tagImport.id}/start_process`).then(() => {
            setProcessing(true)
        }).finally(() => setLoading(false))
    }

    if (processing) {
        return (
            <>
                <LoadingOutlined/> {t('processing-progress', 'Processing progress')}:
                <Progress percent={progress || 0} status={'active'}/>
            </>
        )
    }

    const coreFields = Object.keys(TAG_FIELDS).filter(f => TAG_FIELDS[f].group === 'core')
    const locationFields = Object.keys(TAG_FIELDS).filter(f => TAG_FIELDS[f].group === 'location')
    const productFields = Object.keys(TAG_FIELDS).filter(f => TAG_FIELDS[f].group === 'product')
    const artworkFields = Object.keys(TAG_FIELDS).filter(f => TAG_FIELDS[f].group === 'artwork')

    if (attrs.aasm_state === 'errored') {
        return <Alert type={'error'} showIcon message={t('parse-error', 'Parse Error')} description={`${t('parse-error-description', 'Unable to parse file, please try another.')} (${attrs.parse_error?.message})`}/>
    }

    return (
        <>
            {(attrs.aasm_state === 'analyzing' || attrs.aasm_state === 'pending') && (
                <Alert style={{marginBottom: '2em'}} type={'info'} showIcon message={<><em><LoadingOutlined/> {t('analyzing', 'Analyzing')}...</em></>}/>
            )}

            <VerticalSpace>
                <div>
                    {t('name-column', 'Column containing tag name')}:
                    <br/>
                    <AntSelect
                        name={'name_column'}
                        value={attrs.name_column}
                        onChange={update('name_column')}
                        style={{width: '100%'}}
                        placeholder={t('placeholder-choose-name-column', 'Choose Name Column...')}
                        required
                    >
                        {attrs.columns?.map(col => (
                            <AntSelect.Option value={col.name} key={col.name}>{col.name}</AntSelect.Option>
                        ))}
                    </AntSelect>
                </div>

                <Divider/>

                <Descriptions bordered size='small' column={1}>
                    <Descriptions.Item label={t('filename', 'Filename')}>
                        {attrs.filename}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('rows', 'Rows')}>{attrs.rows}</Descriptions.Item>
                    <Descriptions.Item label={t('existing-matches', 'Existing Tags to Update')}>
                        {attrs.matches}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('note', 'Note')}>
                        <Typography.Text editable={{onChange: update('note')}}>{attrs.note}</Typography.Text>
                    </Descriptions.Item>
                </Descriptions>
            </VerticalSpace>

            <Divider/>

            <Descriptions bordered size='small' column={1} title={t('column-mapping', 'Column Mapping')} className={'tag-import-headers'}>
                {attrs.columns?.map(c => {
                    return (
                        <Descriptions.Item label={c.name} key={c.name} style={{width: '50%'}}>
                            <AntSelect
                                name={'mapping'}
                                value={c.mapping}
                                onChange={val => onChange(c, val)}
                                style={{width: '100%'}}
                                placeholder={t('placeholder-choose-mapping', 'Choose mapping...')}
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) => typeof option.children == 'string' && option.children.toLowerCase().includes(input.toLowerCase())}
                                getPopupContainer={() => document.getElementById('tag-import-form') || document.body}
                            >
                                <AntSelect.OptGroup label={t('core-fields', 'Core Fields')}>
                                    {coreFields.map(f => (
                                        <AntSelect.Option value={f} key={f}>{TAG_FIELDS[f].name}</AntSelect.Option>
                                    ))}
                                </AntSelect.OptGroup>

                                <AntSelect.OptGroup label={t('location-fields', 'Location Fields')}>
                                    {locationFields.map(f => (
                                        <AntSelect.Option value={f} key={f}>{TAG_FIELDS[f].name}</AntSelect.Option>
                                    ))}
                                </AntSelect.OptGroup>

                                <AntSelect.OptGroup label={t('product-fields', 'Product Fields')}>
                                    {productFields.map(f => (
                                        <AntSelect.Option value={f} key={f}>{TAG_FIELDS[f].name}</AntSelect.Option>
                                    ))}
                                </AntSelect.OptGroup>

                                <AntSelect.OptGroup label={t('artwork-fields', 'Artwork Fields')}>
                                    {artworkFields.map(f => (
                                        <AntSelect.Option value={f} key={f}>{TAG_FIELDS[f].name}</AntSelect.Option>
                                    ))}
                                </AntSelect.OptGroup>
                            </AntSelect>
                        </Descriptions.Item>
                    )
                })}
            </Descriptions>

            <Popconfirm title={t('confirm-process-tag-import', 'Process tag import?')} onConfirm={process}>
                <Button icon={<CheckOutlined/>} type={'primary'} block style={{marginTop: '2em'}} loading={loading} disabled={!attrs.name_column || attrs.aasm_state !== 'analyzed'}>{t('button-process-import', 'Process Import')}</Button>
            </Popconfirm>
        </>
    )
}

export {TagImportSetup, NewTagImportButton}
